import config from '@/config';
import isSSR from '@/utils/isSSR';
import { LoggingEnvironment, LoggingSeverity } from '@/utils/logger-lib/logger.type';
import { createApplicationLoggerForBrowser } from '@/utils/logger-lib/logger-browser';

const mapEnvNameToLoggingEnvironment = (envName: typeof config.env): LoggingEnvironment => {
  switch (envName) {
    case 'local':
    case 'development':
      return LoggingEnvironment.Dev;
    case 'staging':
      return LoggingEnvironment.Staging;
    case 'production':
      return LoggingEnvironment.Prod;
    // NOTE: in SSR this will be empty, how can we get environment into browser side? See, src/config/index.ts:70
  }
};

const mapLogLevelToLoggingSeverity = (level: typeof config.LOG_LEVEL): LoggingSeverity => {
  // NOTE: In browser side logging logging severity has
  // following special handling "src/app/components/Head.vue".
  switch (level) {
    case 'DEBUG':
      return LoggingSeverity.Debug;
    case 'WARN':
      return LoggingSeverity.Warn;
    case 'ERROR':
      return LoggingSeverity.Error;
    default:
      // INFO and PROFILE
      return LoggingSeverity.Info;
  }
};
/**
 * Logger instance to be used inside Vue components.
 *
 * Logger works on SSR and client-side.
 *
 * For Node.js code, use {@link @/server/logger.ts} instead.
 *
 * Both loggers have identical interface.
 */
let getRequestId = (): string | null => null;
let requestIdModuleLoaded = false;
const logger = createApplicationLoggerForBrowser({
  applicationName: 'opkoti-frontend',
  environment: mapEnvNameToLoggingEnvironment(config.env),
  loggingSeverity: mapLogLevelToLoggingSeverity(config.LOG_LEVEL),
  dynamicProperties: () => {
    if (isSSR) {
      if (!requestIdModuleLoaded) {
        // Load request-id dynamically once because
        // it cannot be referenced in browser side.
        import('@/server/request-id/request-id')
          .then((mod) => {
            requestIdModuleLoaded = true;
            getRequestId = mod.getRequestId;
          })
          .catch((e: unknown) => {
            // eslint-disable-next-line no-console
            console.error('Failed to load request-id module dynamically', e);
          });
      }

      return {
        requestId: getRequestId() ?? 'n/a',
        ssr: true,
      };
    }

    return {} as Record<string, string>;
  },
});

export default logger;
